import React, { useState, useLayoutEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import Tooltip from "../tooltip/Tooltip";
import { AnimateSharedLayout, AnimatePresence, motion } from "framer-motion";
import Fade from "components/animations/Fade";
import AtlasMenuFavorites from "./AtlasMenuFavorites";
import AtlasMenuFilters from "./AtlasMenuFilters";
import { useResize } from "../../hooks";
import random from "lodash.random";
import AtlasPaginationControls from "./AtlasPaginationControls";
import { useElementQueries } from "../itinerary/utils";

const schema = [
    {
        key: "filters",
        label: "Filters",
        icon: "filters",
    },
    {
        key: "favorites",
        label: "Email favorites",
        icon: "star",
    },
];
const schemaByKey = schema.reduce((result, item) => ({ ...result, [item.key]: item }), {});
const popupVariants = {
    open: {
        opacity: 1,
        translateY: 0,
    },
    closed: {
        opacity: 0,
        translateY: -100,
    },
};
export const motionTransition = {
    duration: 0.1,
    ease: "easeOut",
};

const iOS = ["iPhone Simulator", "iPhone"].includes(navigator.platform);
const containerSizes = [500, 700];

function AtlasMenu(props) {
    const {
        tags,
        showModalCloseBtn,
        showSearchBtn,
        stops,
        isTranslucid,
        favoriteIds,
        filters,
        setFilters,
        setCurrentPage,
        totalCount,
        currentPage,
    } = props;
    const [containerRef, { attrs: containerAttrs }] = useElementQueries(containerSizes);
    const [activeKey, setActiveKey] = useState(null);
    const [overlayStyle, setOverlayStyle] = useState({});
    const [layoutEffectId, setLayoutEffectId] = useState(null);
    const containerClasses = classNames("trpr-atlas-menu__container", {
        "is-translucid": isTranslucid,
    });
    const activeSchema = schemaByKey[activeKey];
    const atlasMenuRef = useRef();
    const atlasMenuEl = atlasMenuRef?.current;
    const closeModal = () => setActiveKey(null);

    // Update overlay position on window resize and modal open event
    useResize(
        useCallback(() => {
            if (!activeKey) {
                return;
            }
            setLayoutEffectId(random(0, 9999));
        }, [activeKey]),
    );
    useLayoutEffect(() => {
        if (!activeKey) {
            return;
        }
        const atlasMenuElBCR = atlasMenuEl.getBoundingClientRect();
        setOverlayStyle({
            width: atlasMenuElBCR.width,
            left: atlasMenuElBCR.x,
            top: atlasMenuElBCR.height,
        });
    }, [activeKey, atlasMenuEl, layoutEffectId]);

    return (
        <div
            ref={containerRef}
            className={classNames(containerClasses, "trpr-atlas-menu-container")}
            {...containerAttrs}
        >
            {createPortal(
                <AnimatePresence initial={true}>
                    {activeKey && (
                        <motion.div
                            onClick={closeModal}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={motionTransition}
                            className="trpr__route-explorer-overlay"
                            style={{ ...overlayStyle, zIndex: 4 }}
                        />
                    )}
                </AnimatePresence>,
                document.querySelector("#portal"),
            )}
            <div
                ref={atlasMenuRef}
                className="trpr-atlas-menu"
                style={{
                    zIndex: 3,
                }}
            >
                <AtlasPaginationControls
                    isTranslucid={isTranslucid}
                    setCurrentPage={setCurrentPage}
                    itemsPerPage={30}
                    totalCount={totalCount}
                    currentPage={currentPage}
                />
                <AnimateSharedLayout>
                    <div className="trpr-atlas-menu__toolbar">
                        {schema.map(item => {
                            if (!showSearchBtn && item.key === "search") {
                                return null;
                            }
                            return (
                                <motion.div
                                    transition={motionTransition}
                                    className={classNames([
                                        "trpr-atlas-menu__toolbar-item",
                                        { "has-counter": ["favorites", "filters"].includes(item.key) },
                                    ])}
                                    key={item.key}
                                >
                                    <motion.div
                                        transition={motionTransition}
                                        aria-hidden="true"
                                        layout="position"
                                        className="trpr-atlas-menu__toolbar-item-line"
                                    />
                                    <div className="trpr__tooltip-primary-wrapper">
                                        <motion.button
                                            transition={motionTransition}
                                            layout="position"
                                            key={item.key}
                                            onClick={() => setActiveKey(item.key)}
                                        >
                                            <i className={`trpr__icon-${item.icon}`} />
                                        </motion.button>
                                        {!activeKey && !(["search", "favorites"].includes(item.key) && iOS) && (
                                            <Tooltip
                                                className={`is-floating trpr__mobile-alt ${
                                                    item.key === "favorites" ? "is-right" : ""
                                                }`}
                                            >
                                                {item.label}
                                            </Tooltip>
                                        )}
                                    </div>
                                    <AnimatePresence>
                                        {item.key === "favorites" && favoriteIds.length > 0 && (
                                            <motion.span
                                                layout="position"
                                                className="trpr-atlas-menu__toolbar-item-counter"
                                                transition={motionTransition}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                            >
                                                {favoriteIds.length}
                                            </motion.span>
                                        )}
                                        {item.key === "filters" && filters.length > 0 && (
                                            <motion.span
                                                layout="position"
                                                className="trpr-atlas-menu__toolbar-item-counter"
                                                transition={motionTransition}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                            >
                                                {filters.length}
                                            </motion.span>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            );
                        })}
                        {showModalCloseBtn && (
                            <button>
                                <i className="trpr__icon-close" />
                            </button>
                        )}
                    </div>
                </AnimateSharedLayout>
            </div>
            <div
                style={{
                    left: 0,
                    position: "absolute",
                    top: 0,
                    width: "100%",
                    zIndex: 3,
                }}
            >
                <AnimatePresence initial={true}>
                    {activeKey && (
                        <Fade key="active" transition={motionTransition}>
                            <div className="trpr-atlas-menu trpr-atlas-menu--secondary">
                                <div className="trpr-atlas-menu__active-title">
                                    <i className={`trpr__icon-${activeSchema.icon}`} />
                                    <span>{activeSchema.label}</span>
                                </div>
                                <div>
                                    <button className="trpr-atlas-menu__active-close" onClick={closeModal}>
                                        <i className="trpr__icon-close" />
                                    </button>
                                </div>
                            </div>
                        </Fade>
                    )}
                </AnimatePresence>
            </div>
            <div
                style={{
                    left: 0,
                    position: "absolute",
                    top: "100%",
                    width: "100%",
                    zIndex: 2,
                }}
            >
                <AnimatePresence>
                    {activeKey !== null && (
                        <motion.div
                            variants={popupVariants}
                            initial="closed"
                            animate="open"
                            exit="closed"
                            transition={motionTransition}
                        >
                            {activeKey === "favorites" && (
                                <AtlasMenuFavorites closeModal={closeModal} favoriteIds={favoriteIds} />
                            )}
                            {activeKey === "filters" && (
                                <AtlasMenuFilters
                                    stops={stops}
                                    closeModal={closeModal}
                                    filters={filters}
                                    setFilters={setFilters}
                                    tags={tags}
                                />
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

AtlasMenu.defaultProps = {
    stops: [],
    favoriteIds: [],
};

export default AtlasMenu;
